import { useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { BUILD_EXTENSIONS, buildLabel, type AppBuildView } from '@1qa/shared';
import { Package, Trash2, Upload } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { formatBytes, timeAgo } from '@/lib/format';
import type { Project } from '@/lib/types';
import { Badge, Button, Card, EmptyState, ErrorNotice, Input, PageLoader, useConfirm, useToast } from '@/components/ui';

/**
 * App builds of a mobile project (docs/design/mobile-runs.md "App builds"): upload an APK, see which runs used
 * which build, delete your own. Runs install the latest build unless the Run button's Build picker names one.
 */
export function BuildsTab({ project, canUpload }: { project: Project; canUpload: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const input = useRef<HTMLInputElement>(null);
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const ext = BUILD_EXTENSIONS[project.platform] ?? '.apk';
  const builds = useQuery({ queryKey: ['builds', project.id], queryFn: () => api.builds(project.id) });

  const upload = useMutation({
    mutationFn: (file: File) => {
      setProgress(0);
      return api.uploadBuild(project.id, file, notes, setProgress);
    },
    onSuccess: (b) => {
      toast.success(`Build ${buildLabel(b)} uploaded`);
      setNotes('');
      qc.invalidateQueries({ queryKey: ['builds', project.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
    onSettled: () => setProgress(null),
  });

  const remove = useMutation({
    mutationFn: (b: AppBuildView) => api.deleteBuild(b.id),
    onSuccess: () => {
      toast.success('Build deleted');
      qc.invalidateQueries({ queryKey: ['builds', project.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pick = (files: FileList | null) => {
    const file = files?.[0];
    if (!file || upload.isPending) return;
    if (!file.name.toLowerCase().endsWith(ext)) {
      toast.error(file.name.toLowerCase().endsWith('.aab') ? 'Upload an APK: an .aab cannot be installed directly.' : `Choose an ${ext} file.`);
      return;
    }
    upload.mutate(file);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (canUpload) pick(e.dataTransfer.files);
  };

  if (builds.isPending) return <PageLoader />;
  if (builds.isError) return <ErrorNotice error={builds.error} onRetry={() => builds.refetch()} />;

  const uploader = canUpload && (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={clsx(
        'flex flex-wrap items-center gap-3 border-b border-border px-5 py-4 transition-colors',
        dragging && 'bg-primary-subtle',
      )}
    >
      <input ref={input} type="file" accept={`${ext},application/vnd.android.package-archive`} className="sr-only" onChange={(e) => pick(e.target.files)} data-testid="build-file" />
      <Button onClick={() => input.current?.click()} loading={upload.isPending} icon={<Upload className="size-4" />}>
        Upload {ext.slice(1).toUpperCase()}
      </Button>
      <Input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional), e.g. Release 2.4 RC1"
        maxLength={500}
        aria-label="Notes for the next upload"
        className="w-72 max-w-full"
      />
      <p className="text-xs text-fg-muted">or drop the file here. The package name must be {project.appId ? <code>{project.appId}</code> : "the project's"}.</p>
      {progress !== null && (
        <div className="flex w-full items-center gap-3" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
          <div className="h-1.5 flex-1 overflow-hidden rounded-bar bg-surface-raised">
            <div className="h-full bg-primary transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <span className="text-xs text-fg-secondary tabular-nums">{progress < 1 ? `${Math.round(progress * 100)}%` : 'Checking…'}</span>
        </div>
      )}
    </div>
  );

  if (builds.data.length === 0) {
    return (
      <Card>
        {uploader}
        <EmptyState
          className="m-5"
          icon={<Package className="size-6" />}
          title="No app builds yet"
          description={`Upload the ${ext} you want to test. Every run installs the latest build on a clean device unless you pick another one next to the Run button.`}
        />
      </Card>
    );
  }

  return (
    <Card>
      {uploader}
      <ul className="divide-y divide-border-subtle">
        {builds.data.map((b, i) => (
          <li key={b.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-testid="build-row">
            <span className="grid size-9 place-items-center rounded-card bg-primary-subtle text-primary">
              <Package className="size-[18px]" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
                {buildLabel(b)}
                {i === 0 && <Badge tone="info">Latest</Badge>}
              </div>
              <div className="truncate text-xs text-fg-secondary" title={b.sha256}>
                {b.fileName} · {formatBytes(b.sizeBytes)}
                {b.packageName && ` · ${b.packageName}`} · by {b.uploadedBy.name} {timeAgo(b.createdAt)}
                {` · used by ${b.runCount} run${b.runCount === 1 ? '' : 's'}`}
              </div>
              {b.notes && <div className="mt-0.5 truncate text-xs text-fg-muted">{b.notes}</div>}
            </div>
            {b.canDelete && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Delete build ${buildLabel(b)}`}
                loading={remove.isPending && remove.variables?.id === b.id}
                onClick={async () => {
                  const ok = await confirm({
                    title: `Delete build ${buildLabel(b)}?`,
                    message: 'The file is removed. Reports of runs that used it keep its name.',
                    confirmLabel: 'Delete build',
                  });
                  if (ok) remove.mutate(b);
                }}
                icon={<Trash2 className="size-4" />}
              />
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
